import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Layout } from '../components/Layout';
import { adminApiService } from '../services/api';
import { Assigner, DirectoryUser } from '../types';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export const Assigners: React.FC = () => {
  const [assigners, setAssigners] = useState<Assigner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add assigner dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DirectoryUser | null>(null);

  // Manage states dialog
  const [statesDialogOpen, setStatesDialogOpen] = useState(false);
  const [editingAssigner, setEditingAssigner] = useState<Assigner | null>(null);
  const [draftStates, setDraftStates] = useState<string[]>([]);
  const [savingStates, setSavingStates] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const loadAssigners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.getAssigners();
      if (response.success && response.data) {
        setAssigners(response.data);
      }
    } catch {
      setError('Failed to load assigners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssigners();
  }, [loadAssigners]);

  // Directory search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await adminApiService.searchDirectory(searchQuery);
        if (response.success && response.data) {
          setSearchResults(response.data);
        }
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleAddAssigner = async () => {
    if (!selectedUser) return;
    try {
      const response = await adminApiService.createAssigner({
        azureUserId: selectedUser.id,
        email: selectedUser.email,
        name: selectedUser.displayName,
      });
      if (response.success) {
        setSnackbar({ open: true, message: `Added ${selectedUser.displayName} as assigner`, severity: 'success' });
        setAddDialogOpen(false);
        setSelectedUser(null);
        setSearchQuery('');
        loadAssigners();
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to add assigner', severity: 'error' });
    }
  };

  const handleDeactivateAssigner = async (assigner: Assigner) => {
    if (!window.confirm(`Remove ${assigner.name} as an assigner?`)) return;
    try {
      const response = await adminApiService.deactivateAssigner(assigner.id);
      if (response.success) {
        setSnackbar({ open: true, message: `Removed ${assigner.name}`, severity: 'success' });
        loadAssigners();
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove assigner', severity: 'error' });
    }
  };

  const handleOpenStatesDialog = (assigner: Assigner) => {
    setEditingAssigner(assigner);
    setDraftStates([...assigner.states]);
    setStatesDialogOpen(true);
  };

  const handleSaveStates = async () => {
    if (!editingAssigner) return;
    setSavingStates(true);
    try {
      const response = await adminApiService.setAssignerStates(editingAssigner.id, draftStates);
      if (response.success) {
        setSnackbar({ open: true, message: 'States updated', severity: 'success' });
        setStatesDialogOpen(false);
        setEditingAssigner(null);
        loadAssigners();
      } else {
        setSnackbar({ open: true, message: 'Failed to update states', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to update states', severity: 'error' });
    } finally {
      setSavingStates(false);
    }
  };

  return (
    <Layout pageTitle="Assigner Management">
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Manage assigners and the states they cover.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Assigner
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : assigners.length === 0 ? (
          <Alert severity="info">No assigners configured yet. Click "Add Assigner" to get started.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>States</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assigners.map((assigner) => (
                  <TableRow key={assigner.id} hover>
                    <TableCell>{assigner.name}</TableCell>
                    <TableCell>{assigner.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {assigner.states.length === 0 ? (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        ) : (
                          assigner.states.map(s => (
                            <Chip key={s} label={s} size="small" variant="outlined" />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assigner.isActive ? 'Active' : 'Inactive'}
                        color={assigner.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit states">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenStatesDialog(assigner)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove assigner">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeactivateAssigner(assigner)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add Assigner Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Assigner</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Search the company directory to find and add an assigner.
          </Typography>
          <Autocomplete
            options={searchResults}
            getOptionLabel={(option) => `${option.displayName} (${option.email})`}
            loading={searching}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            onInputChange={(_, value) => setSearchQuery(value)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body1">{option.displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">{option.email}</Typography>
                  {option.jobTitle && (
                    <Typography variant="caption" color="text.secondary">{option.jobTitle}</Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by name or email"
                placeholder="Start typing to search..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={searchQuery.length < 2 ? 'Type at least 2 characters' : 'No users found'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Note: Adding someone as an assigner here does not automatically grant them the Assigner role in Azure AD.
            You will need to assign the role separately in Azure AD for them to access the admin portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setSelectedUser(null); setSearchQuery(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddAssigner} disabled={!selectedUser}>
            Add Assigner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage States Dialog */}
      <Dialog
        open={statesDialogOpen}
        onClose={() => setStatesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAssigner ? `States for ${editingAssigner.name}` : 'States'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Select one or more states this assigner will cover. Their scheduling view will
            default to reporters whose primary state is one of these.
          </Typography>
          <Autocomplete
            multiple
            options={US_STATES}
            value={draftStates}
            onChange={(_, value) => setDraftStates(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="States" placeholder="Add state..." />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatesDialogOpen(false)} disabled={savingStates}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveStates}
            disabled={savingStates}
          >
            {savingStates ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};
